import type { ReactNode } from "react";
import { useLocaleState, useTranslate } from "ra-core";
import { AutocompleteArrayInput } from "@/components/admin/autocomplete-array-input";
import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import { AsideSection } from "../misc/AsideSection";
import { RatingInput, RatingStars } from "../misc/Rating";
import { isEmptyValue } from "../misc/isEmptyValue";
import { ToDefine } from "../misc/ToDefine";
import { formatCurrency, formatDate, formatNumber } from "./format";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type {
  CustomFieldDefinition,
  CustomFieldResource,
  CustomFieldValue,
  CustomFieldValues,
} from "../types";

const booleanChoices = [
  { id: "true", name: "ra.boolean.true" },
  { id: "false", name: "ra.boolean.false" },
];

const formatBoolean = (value: unknown) =>
  value === true ? "true" : value === false ? "false" : "";

const parseBoolean = (value: unknown) =>
  value === "true" ? true : value === "false" ? false : null;

const useCustomFieldDefinitions = (resource: CustomFieldResource) =>
  useConfigurationContext().customFields?.[resource] ?? [];

/** Form section with one input per custom field. None of them is required. */
export const CustomFieldsInputs = ({
  resource,
}: {
  resource: CustomFieldResource;
}) => {
  const translate = useTranslate();
  const definitions = useCustomFieldDefinitions(resource);
  if (definitions.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("crm.custom_fields.title")}
      </h6>
      {definitions.map((definition) => (
        <CustomFieldInput key={definition.key} definition={definition} />
      ))}
    </div>
  );
};

const CustomFieldInput = ({
  definition,
}: {
  definition: CustomFieldDefinition;
}) => {
  const { currency } = useConfigurationContext();
  const source = `custom_fields.${definition.key}`;
  const label = definition.label;
  const options = definition.options ?? [];

  switch (definition.type) {
    case "long_text":
      return (
        <TextInput source={source} label={label} multiline helperText={false} />
      );
    case "number":
      return <NumberInput source={source} label={label} helperText={false} />;
    case "currency":
      return (
        <NumberInput
          source={source}
          label={`${label} (${currency})`}
          helperText={false}
        />
      );
    case "date":
      return <DateInput source={source} label={label} helperText={false} />;
    case "select":
      return (
        <SelectInput
          source={source}
          label={label}
          choices={options}
          optionText="label"
          optionValue="value"
          translateChoice={false}
          helperText={false}
        />
      );
    case "multi_select":
      return (
        <AutocompleteArrayInput
          source={source}
          label={label}
          choices={options}
          optionText="label"
          optionValue="value"
          translateChoice={false}
          helperText={false}
        />
      );
    case "boolean":
      return (
        <SelectInput
          source={source}
          label={label}
          choices={booleanChoices}
          format={formatBoolean}
          parse={parseBoolean}
          helperText={false}
        />
      );
    case "rating":
      return <RatingInput source={source} label={label} />;
    case "url":
      return (
        <TextInput
          source={source}
          label={label}
          type="url"
          helperText={false}
        />
      );
    default:
      return <TextInput source={source} label={label} helperText={false} />;
  }
};

/** Aside section listing every custom field, with « À définir » for empty ones. */
export const CustomFieldsAside = ({
  resource,
  values,
}: {
  resource: CustomFieldResource;
  values?: CustomFieldValues;
}) => {
  const translate = useTranslate();
  const definitions = useCustomFieldDefinitions(resource);
  if (definitions.length === 0) return null;

  return (
    <AsideSection title={translate("crm.custom_fields.title")}>
      {definitions.map((definition) => (
        <FieldRow key={definition.key} label={definition.label}>
          <CustomFieldValueView
            definition={definition}
            value={values?.[definition.key]}
          />
        </FieldRow>
      ))}
    </AsideSection>
  );
};

/** Label above its value, used by the prospecting and custom field sections. */
export const FieldRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col mb-1">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span>{children}</span>
  </div>
);

export const CustomFieldValueView = ({
  definition,
  value,
}: {
  definition: CustomFieldDefinition;
  value: CustomFieldValue | undefined;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { currency } = useConfigurationContext();

  if (isEmptyValue(value)) return <ToDefine />;

  const optionLabel = (optionValue: string) =>
    definition.options?.find((option) => option.value === optionValue)?.label ??
    optionValue;

  switch (definition.type) {
    case "select":
      return <>{optionLabel(String(value))}</>;
    case "multi_select":
      return (
        <>
          {(Array.isArray(value) ? value : [String(value)])
            .map(optionLabel)
            .join(", ")}
        </>
      );
    case "boolean":
      return (
        <>
          {translate(value === true ? "ra.boolean.true" : "ra.boolean.false")}
        </>
      );
    case "rating":
      return <RatingStars value={Number(value)} />;
    case "number":
      return <>{formatNumber(value, locale)}</>;
    case "currency":
      return <>{formatCurrency(value, locale, currency)}</>;
    case "date":
      return <>{formatDate(value, locale)}</>;
    case "url":
      return (
        <a
          className="underline hover:no-underline break-all"
          href={
            String(value).startsWith("http")
              ? String(value)
              : `https://${value}`
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          {String(value)}
        </a>
      );
    default:
      return <span className="whitespace-pre-line">{String(value)}</span>;
  }
};
